/* inno-smooth.js — weighted, eased scrolling for the archive's scroll panes.
   A single document-level wheel handler drives the nearest known scroll pane toward
   a target with TIME-BASED exponential smoothing, so the weight/after-lag feels the
   same on a 60Hz or a 144Hz display (a per-frame lerp would race on high-refresh
   screens and feel native). Desktop wheel only; touch keeps its native momentum. */
(function () {
  'use strict';

  // touch devices keep native momentum; everything else gets the weighted scroll
  if (window.matchMedia && !window.matchMedia('(pointer: fine)').matches) return;

  /* NOT ON A PRODUCT PAGE. This file takes over the wheel: a window-level listener that is NOT
     passive, cancels the native scroll, and replays it through an ease written to scrollTop
     every frame. That moves scrolling off the browser's own scrolling thread and onto the main
     thread, where anything else in the frame (images decoding, the page's own listeners) costs
     frames; it trails the wheel by the 210ms it was tuned for; and it writes whole pixels, so
     the slow end of every glide steps. On the shop lattice that weight was a choice. On a
     product page it read as exactly what it is — choppy and late — so the page gets the
     browser's own scrolling, which on every desk is already smooth, momentum-correct and
     tuned to the trackpad or wheel in hand.
     Returning before the listener is added matters: a non-passive wheel listener that merely
     returns early still makes the browser wait on the main thread for every wheel event. */
  if (document.body && document.body.classList.contains('template-product')) return;

  // Weight. 120ms braked almost as soon as the wheel stopped, which reads as a cheap
  // "smooth scroll" plug-in; 210 carries the glide a beat further so the pane feels like it
  // has mass. SCALE stretches each notch so a flick actually travels — weight without it just
  // feels sluggish. Both stay framerate-independent (see the exp() ease below).
  var TAU = 210;   // smoothing time-constant in ms — larger = longer, heavier glide
  var SCALE = 1.35;  // wheel-delta multiplier — more travel per notch
  var SEL = '#shop, #archive, .inno-detail, .inno-gallery, #designer-modal';
  var panes = new WeakMap();

  function docScroller() { return document.scrollingElement || document.documentElement; }

  // vertical first, else horizontal — null if the element has nothing to scroll
  function axisFor(el) {
    if (el.scrollHeight > el.clientHeight + 1) return { pos: 'scrollTop', size: 'scrollHeight', client: 'clientHeight' };
    if (el.scrollWidth > el.clientWidth + 1) return { pos: 'scrollLeft', size: 'scrollWidth', client: 'clientWidth' };
    return null;
  }

  function stateFor(el, axis) {
    var st = panes.get(el);
    if (!st) {
      st = { target: el[axis.pos], current: el[axis.pos], running: false, last: 0 };
      panes.set(el, st);
      // scrollbar drag / programmatic jumps resync so we never fight them.
      // the document scroller reports its scroll on window, not the element.
      (el === docScroller() ? window : el).addEventListener('scroll', function () {
        if (!st.running) { st.target = el[axis.pos]; st.current = el[axis.pos]; }
      }, { passive: true });
    }
    return st;
  }

  window.addEventListener('wheel', function (e) {
    if (e.ctrlKey) return;                                   // pinch-zoom
    var el = e.target && e.target.closest && e.target.closest(SEL);
    var axis = el ? axisFor(el) : null;

    // no scrollable pane under the cursor → drive the whole-page (window) scroll with the
    // same weighted easing, so product pages feel identical to the shop lattice
    if (!axis) {
      var ds = docScroller();
      axis = axisFor(ds);
      if (!axis) return;                                     // nothing scrolls anywhere → let it bubble
      el = ds;
    }

    var st = stateFor(el, axis);
    var max = el[axis.size] - el[axis.client];
    if (max <= 0) return;                              // nothing to scroll → let it bubble
    // a fresh gesture (not mid-momentum) re-syncs to the real position — this kills the
    // glitch at the top edge where native + eased scroll fought over the same event
    if (!st.running) { st.current = el[axis.pos]; st.target = el[axis.pos]; }
    e.preventDefault();
    var mult = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el[axis.client] : 1;
    st.target = Math.max(0, Math.min(st.target + (e.deltaY || e.deltaX) * mult * SCALE, max));

    if (!st.running) {
      st.running = true;
      st.last = performance.now();
      requestAnimationFrame(function loop(now) {
        var dt = Math.min(50, now - st.last); st.last = now;
        var d = st.target - st.current;
        st.current += d * (1 - Math.exp(-dt / TAU));   // framerate-independent ease-out
        if (Math.abs(st.target - st.current) < 0.2) {   // tighter than 0.4 — a longer glide
                                                        // makes an early cutoff visible as a snap
          st.current = st.target; el[axis.pos] = st.target; st.running = false; return;
        }
        el[axis.pos] = Math.round(st.current);
        requestAnimationFrame(loop);
      });
    }
  }, { passive: false });

  window.innoSmooth = function () {};   // auto — kept for API compatibility
})();
